import { Trans, useLingui } from "@lingui/react/macro";
import { useContext, useEffect, useRef, useState } from "react";
import Chat from "@medoma/components/Chat/Chat";
import { Loading } from "@medoma/components/Loading/Loading";
import ChatContext from "@medoma/chat/ChatContext";
import {
  DISPLAYED_EVENT_TYPES,
  formatTimestamp,
  makeMessages,
  useMatrixTimelineEvents,
} from "@medoma/chat/useMatrixEvents";
import { useMatrixRoom } from "@medoma/chat/useMatrixRoom";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import { sendTextMessage } from "@medoma/chat/matrixClient";
import { getEmployeeRoomAlias } from "@medoma/chat/roomAlias";
import { sendReadReceiptOnDemand } from "@medoma/chat/useSendReadReceipt";
import { useChatScroll } from "@medoma/chat/useChatScroll";
import { SyncState } from "matrix-js-sdk";
import { useMatrixConnectionStatus } from "@medoma/chat/useMatrixConnectionStatus";
import {
  lastEventReadByUser,
  lastEventReadByUserOtherThanUserId,
  useLastReadEventPerUser,
} from "@medoma/chat/useLastReadEvent";
import { useCurrentEmployee, useEmployee } from "@/api/Employee";
import styles from "./ChatWithGo.module.scss";
import Portrait from "@medoma/components/Portrait/Portrait";
import { Heading } from "@medoma/components/Heading/Heading";
import { Text } from "@medoma/components/Text/Text";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import { getHistory } from "@medoma/chat/getHistory";
import { employeeName, medicalTitle } from "@medoma/models/shifts";
import * as Sentry from "@sentry/react";
import { useOtherUserIsTyping } from "@medoma/chat/useOtherUserIsTyping";
import { useSendTypingNotification } from "@medoma/chat/useSendTypingNotification";
import { useUserProfile } from "@/api/Users";
import { CHAT_ENVIRONMENT } from "@/Utils/EnvUtils";
import { useSelectedSite } from "@/Utils/useSelectedSite";
import { BackButton } from "@/components/NavigationButtons/NavigationButtons";

const ChatWithGo = () => {
  const { t } = useLingui();
  const { organization } = useUserProfile();
  const { employeeId } = z
    .object({ employeeId: z.string() })
    .parse(useParams());
  const { matrixClient } = useContext(ChatContext);
  const { countryCode } = useUserProfile();
  const navigate = useNavigate();

  const [pendingMessage, setPendingMessage] = useState("");
  const { data: centerUser } = useCurrentEmployee();

  const selectedSite = useSelectedSite();
  if (!selectedSite) {
    throw new Error("Should have selected site when showing employee chat");
  }

  const { data: goEmployee, isError, error } = useEmployee(Number(employeeId));
  const roomAlias = getEmployeeRoomAlias({
    employeeId,
    env: CHAT_ENVIRONMENT,
    matrixClient,
    organization,
    site: selectedSite,
  });
  const chatConnection = useMatrixConnectionStatus(matrixClient);
  const { roomId, getRoom } = useMatrixRoom(matrixClient, roomAlias);
  const matrixUserId = matrixClient?.getUserId();
  const { otherUserIsTyping } = useOtherUserIsTyping({
    matrixClient,
  });
  const { sendNoLongerTypingNotification, updateTypingState } =
    useSendTypingNotification({ matrixClient, roomId });

  // Reset all room specific state when chatting with another employee
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setPendingMessage("");
    return () => {
      sendNoLongerTypingNotification();
    };
  }, [employeeId, sendNoLongerTypingNotification]);

  const timelineEvents = useMatrixTimelineEvents(matrixClient, roomId);

  const messages = matrixUserId
    ? makeMessages(timelineEvents, matrixUserId)
    : [];

  const latestMessage = messages[messages.length - 1]?.id ?? null;

  const messagePanelRef = useRef<HTMLDivElement>(null);
  const inputTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const { scrollMessagePanelToBottom } = useChatScroll({
    messagePanelRef,
    inputTextAreaRef,
    messages,
  });

  const sendMessage = () => {
    if (!matrixClient || !roomId) {
      return;
    }
    sendTextMessage(
      matrixClient,
      roomId,
      pendingMessage,
      () => setPendingMessage(""),
      centerUser
        ? employeeName(centerUser, { countryCode, viewer: "colleague" })
        : undefined,
      centerUser?.imageURL ?? null,
      centerUser?.competence ?? undefined,
    );
    sendNoLongerTypingNotification();
  };

  const lastReadEventBySomeNonCenterUser = lastEventReadByUserOtherThanUserId(
    useLastReadEventPerUser({ getRoom }),
    matrixUserId ?? "",
  );

  const shouldRenderInfoPanel =
    otherUserIsTyping || chatConnection === SyncState.Error;

  const lastReadEventReadByCenterUser = lastEventReadByUser(
    useLastReadEventPerUser({ getRoom }),
    matrixUserId ?? "",
  );
  // Empty room (no message) scenario is a -1 to -1 comparison, which happens to work well.
  const lastEventReadByCenterUserIsBeforeLastEventSentByNonCenterUser =
    timelineEvents.findIndex(
      ({ event: { event_id } }) =>
        event_id === lastReadEventReadByCenterUser?.eventId,
    ) <
    // Instead of `findLastIndex`, which cannot be used due to modern browser requirement
    timelineEvents.length -
      1 -
      [...timelineEvents].reverse().findIndex(
        (event) =>
          event.getSender() !== matrixUserId &&
          // Only compare with the event types that we show
          // Otherwise there might be an unshown event sent later, causing the comparison to fail
          // E.g. when clicking "show history" in an empty chat room
          DISPLAYED_EVENT_TYPES.some(
            (displayedEventType) => event.getType() === displayedEventType,
          ),
      );

  const shouldShowMarkAsReadButton =
    lastEventReadByCenterUserIsBeforeLastEventSentByNonCenterUser;

  const room = getRoom();
  if (!matrixClient || !room || !goEmployee) {
    return <Loading message={t`Laddar chatt`} padding={24} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta anställd.`} ${deducedError(error)}`}
        padding={24}
      />
    );
  }

  const goEmployeeCollegialName = employeeName(goEmployee, {
    countryCode,
    viewer: "colleague",
  });
  const goEmployeeFullName = employeeName(goEmployee, {
    employeeNameFormat: "full",
  });

  return (
    <div className={styles.chatWithGoContainer}>
      <div className={styles.backButton}>
        <BackButton
          navigateBack={() => navigate("..")}
          ariaLabel={t`Tillbaka till chattar`}
        />
      </div>
      <div className={styles.chatConversationContainer}>
        <Chat key={goEmployee.id}>
          <Chat.HeaderPanel>
            <div className={styles.chatHeader}>
              <div className={styles.person}>
                <Portrait
                  name={goEmployeeFullName}
                  photoUrl={goEmployee.imageURL}
                  size="large"
                />
                <div className={styles.text}>
                  <Text element="span">
                    <Trans>Chatt med</Trans>
                  </Text>
                  <Heading level="h2">{goEmployeeFullName}</Heading>
                </div>
              </div>
            </div>
          </Chat.HeaderPanel>
          <Chat.MessagePanel
            getHistory={() => getHistory(matrixClient, room)}
            ref={messagePanelRef}
          >
            {messages.map((message) =>
              message.sender.isMe ? (
                <Chat.MessageGroup
                  key={message.id}
                  sender="center-user"
                  status={message.status}
                  timestamp={formatTimestamp(message.epochTimestamp)}
                  senderName={
                    message.sender.name ??
                    t({ message: `Medarbetare`, context: "singular" })
                  }
                  senderTitle={
                    medicalTitle(message.sender.title, { format: "short" }) ??
                    null
                  }
                  readIndicator={
                    message.id === lastReadEventBySomeNonCenterUser?.eventId
                      ? lastReadEventBySomeNonCenterUser.timestamp
                      : undefined
                  }
                  isLatestMessage={latestMessage === message.id}
                >
                  <Chat.Message
                    type="sent-by-me"
                    message={message.text}
                    enableClickableLinks={true}
                  />
                </Chat.MessageGroup>
              ) : (
                <Chat.MessageGroup
                  key={message.id}
                  sender="other-person"
                  senderName={goEmployeeCollegialName}
                  senderPhotoURL={goEmployee.imageURL}
                  senderTitle={
                    medicalTitle(goEmployee.competence, { format: "short" }) ??
                    null
                  }
                  status={message.status}
                  timestamp={formatTimestamp(message.epochTimestamp)}
                >
                  <Chat.Message
                    type="received"
                    message={message.text}
                    enableClickableLinks={true}
                  />
                </Chat.MessageGroup>
              ),
            )}
          </Chat.MessagePanel>
          {shouldShowMarkAsReadButton ? (
            <Chat.InfoPanel type="info">
              <Chat.MarkAsReadButton
                markAsRead={() =>
                  sendReadReceiptOnDemand(
                    matrixClient,
                    timelineEvents,
                    matrixUserId,
                  )
                }
              />
            </Chat.InfoPanel>
          ) : null}
          {shouldRenderInfoPanel ? (
            <Chat.InfoPanel
              type={chatConnection === SyncState.Error ? "error" : "info"}
            >
              {chatConnection === SyncState.Error ? (
                <Trans>Chatten fungerar inte just nu</Trans>
              ) : (
                <Chat.IsTyping text={t`${goEmployeeCollegialName} skriver`} />
              )}
            </Chat.InfoPanel>
          ) : (
            <></>
          )}

          <Chat.InputPanel>
            <Chat.InputTextArea
              inputTextAreaRef={inputTextAreaRef}
              value={pendingMessage}
              onChange={(event) => {
                setPendingMessage(event.currentTarget.value);
                updateTypingState({ event, pendingMessage });
              }}
              onFocus={scrollMessagePanelToBottom}
              placeholder={t`Skriv ett meddelande till ${goEmployeeCollegialName}`}
            />
            <Chat.SendMessageButton
              aria-label={t`Skicka`}
              disabled={pendingMessage.trim() === ""}
              onClick={() => {
                sendMessage();
                sendReadReceiptOnDemand(
                  matrixClient,
                  timelineEvents,
                  matrixUserId,
                );
              }}
            />
          </Chat.InputPanel>
        </Chat>
      </div>
    </div>
  );
};

export default ChatWithGo;
